import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Trees, Building2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

export const TrainingLocationsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const locations = [
    {
      name: 'Dempsey Field',
      address: '71G Loewen Road, Singapore 248847',
      description: 'Our primary training ground nestled in the lush greenery of Dempsey Hill. A spacious venue perfect for developing young talent in a serene environment.',
      icon: Trees,
      features: ['Natural grass pitch', 'SCC Clubhouse with changing rooms & showers', 'F&B at SCC Clubhouse and Dempsey Hill', 'Free parking nearby']
    },
    {
      name: 'The Padang',
      address: 'St Andrew\'s Road, Singapore 178959',
      description: 'Singapore\'s iconic heritage field in the heart of the city. A National Monument and on UNESCO\'s tentative World Heritage list, The Padang connects our players to over 170 years of sporting history.',
      icon: Building2,
      features: ['National Monument & UNESCO tentative list', 'SCC Clubhouse access', 'Full changing rooms, showers & F&B']
    }
  ];

  return (
    <section id="locations" className="min-h-screen py-24 px-6 bg-charcoal">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              Where We Train
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              Training Locations
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              World-class venues for developing the next generation of footballers
            </p>
          </motion.div>

          {/* Location Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {locations.map((location, index) => (
              <motion.div key={location.name} variants={itemVariants}>
                <Card className="bg-gradient-card gold-border overflow-hidden h-full">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <location.icon className="h-8 w-8 text-primary" />
                      </div>
                      <div className="p-3 rounded-lg bg-primary/10">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="font-display text-2xl text-gradient-gold">
                      {location.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-primary/80 text-sm mb-3 font-medium">
                      📍 {location.address}
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {location.description}
                    </p>
                    <div className="space-y-2">
                      {location.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="text-card-foreground text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Coming Soon */}
          <motion.div variants={itemVariants}>
            <Card className="bg-gradient-card gold-border overflow-hidden border-dashed">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-primary/10">
                    <Plus className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <h3 className="font-display text-xl text-primary mb-2">
                  More Locations Coming Soon
                </h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  We are actively expanding our training venues to provide more options and convenience for our academy players and their families.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrainingLocationsSection;
